import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import Logger from '../utils/logger';

/**
 * OAuth Callback Handler Page
 *
 * This page is opened in a popup by the OAuth flow.
 * It receives the callback from GitHub/GitLab and:
 * 1. Parses the query parameters (state, success, error, etc.)
 * 2. Sends a postMessage to the parent window
 * 3. Closes itself
 *
 * Flow:
 * 1. RepositoryConnector.tsx opens popup to auth_url
 * 2. User authorizes on GitHub/GitLab
 * 3. GitHub/GitLab redirects to API callback
 * 4. API redirects popup to: http://localhost:3000/oauth-callback?state=...&success=true
 * 5. This page sends postMessage to parent
 * 6. Parent window receives message and knows auth succeeded
 *
 * Security: Uses React JSX rendering to prevent XSS attacks from malicious URL parameters
 */

type CallbackState = 'loading' | 'success' | 'error';

const OAuthCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [callbackState, setCallbackState] = useState<CallbackState>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [errorDetails, setErrorDetails] = useState<string>('');

  useEffect(() => {
    // Get all query parameters
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');
    const state = searchParams.get('state');
    const repoId = searchParams.get('repo_id');
    const provider = searchParams.get('provider');
    const user = searchParams.get('user');

    Logger.info(`OAuth callback received: success=${success}, error=${error}`);

    // Determine if this is a success or error
    if (success === 'true' || (!error && state)) {
      // Success case
      Logger.info('OAuth authorization successful!');

      const successMsg = {
        type: 'oauth_success',
        state: state,
        repoId: repoId,
        provider: provider,
        user: user,
        message: 'Repository connected successfully'
      };

      // Try postMessage first (works if opener is same-origin)
      if (window.opener) {
        try {
          window.opener.postMessage(successMsg, window.location.origin);
        } catch {
          // Cross-origin — opener blocked by browser
        }
      }

      // Also store in localStorage so parent can detect via storage event or polling
      localStorage.setItem('oauth_callback_result', JSON.stringify(successMsg));

      setCallbackState('success');

      // Close popup after 2 seconds
      setTimeout(() => {
        window.close();
      }, 2000);
    } else if (error) {
      // Error case
      Logger.error(`OAuth error: ${error} - ${errorDescription}`);

      const errorMsg = {
        type: 'oauth_error',
        state: state,
        error: error,
        errorDescription: errorDescription || 'Unknown error',
        message: errorDescription || 'OAuth authentication failed'
      };

      // Try postMessage first
      if (window.opener) {
        try {
          window.opener.postMessage(errorMsg, window.location.origin);
        } catch {
          // Cross-origin — opener blocked by browser
        }
      }

      // Also store in localStorage so parent can detect
      localStorage.setItem('oauth_callback_result', JSON.stringify(errorMsg));

      // Set sanitized error messages (React will escape them)
      setErrorMessage(error || 'Unknown error');
      setErrorDetails(errorDescription || 'OAuth authentication failed');
      setCallbackState('error');

      // Close popup after 3 seconds
      setTimeout(() => {
        window.close();
      }, 3000);
    } else {
      // Unknown state - still loading
      Logger.warn('OAuth callback: Unknown state, waiting for parameters...');
      setCallbackState('loading');
    }
  }, [searchParams]);

  // Success view
  if (callbackState === 'success') {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <div style={{ textAlign: 'center' }}>
          <CheckCircleIcon style={{ width: '60px', height: '60px', marginBottom: '20px', color: '#22c55e' }} />
          <h1 style={{ margin: '0 0 10px 0', fontSize: '28px' }}>Success!</h1>
          <p style={{ margin: '0 0 30px 0', fontSize: '16px', opacity: 0.9 }}>
            Repository connected successfully!
          </p>
          <p style={{ margin: '0', fontSize: '14px', opacity: 0.8 }}>
            This window will close automatically...
          </p>
        </div>
      </div>
    );
  }

  // Error view
  if (callbackState === 'error') {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        color: 'white'
      }}>
        <div style={{ textAlign: 'center' }}>
          <XCircleIcon style={{ width: '60px', height: '60px', marginBottom: '20px', color: '#ef4444' }} />
          <h1 style={{ margin: '0 0 10px 0', fontSize: '28px' }}>Error!</h1>
          <p style={{ margin: '0 0 10px 0', fontSize: '16px', opacity: 0.9 }}>
            {errorMessage}
          </p>
          <p style={{ margin: '0 0 30px 0', fontSize: '14px', opacity: 0.8 }}>
            {errorDetails}
          </p>
          <p style={{ margin: '0', fontSize: '14px', opacity: 0.8 }}>
            This window will close automatically...
          </p>
        </div>
      </div>
    );
  }

  // Loading view (default)
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white'
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          border: '4px solid rgba(255,255,255,0.3)',
          borderRadius: '50%',
          borderTopColor: 'white',
          width: '50px',
          height: '50px',
          margin: '0 auto 20px',
          animation: 'spin 1s linear infinite'
        }}></div>
        <h1 style={{ margin: '0 0 10px 0', fontSize: '24px' }}>Loading...</h1>
        <p style={{ margin: '0', fontSize: '14px', opacity: 0.8 }}>
          Processing OAuth callback...
        </p>
      </div>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default OAuthCallbackPage;
